import { useState } from "react";

import { SpanStatus } from "../../utils/calculateSpanStatus";
import { getNodeTypeConfig, getSpanDisplayType } from "../utils";

import { SpanTooltip } from "./SpanTooltip";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { SpanNode } from "@/types/spans";
import { cn } from "@/lib/utils";

interface SpanBarsProps {
  spans: SpanNode[];
  spanStatuses: Map<string, SpanStatus>;
  traceStartTime: number;
  traceDuration: number;
  maxRows: number;
  rowHeight: number;
  calculateWaterfallPosition: (
    spanNode: SpanNode,
    traceStartTime: number,
    traceDuration: number,
  ) => { left: string; width: string; isRunning: boolean };
  onSpanClick: (span: SpanNode) => void;
}

export const SpanBars = ({
  spans,
  spanStatuses,
  traceStartTime,
  traceDuration,
  maxRows,
  rowHeight,
  calculateWaterfallPosition,
  onSpanClick,
}: SpanBarsProps) => {
  const [hoveredSpanId, setHoveredSpanId] = useState<string | null>(null);
  const spanMap = new Map<string, SpanNode>();
  spans.forEach(span => spanMap.set(span.span_id, span));

  const handleSpanClick = (span: SpanNode) => {
    onSpanClick(span);
  };

  const assignRows = (spansToAssign: SpanNode[]): Map<string, number> => {
    const rowAssignments = new Map<string, number>();
    const rowOccupancies: { start: number; end: number }[][] = [];

    const sortedSpans = [...spansToAssign].sort((a, b) => {
      const aStart = new Date(a.start).getTime();
      const bStart = new Date(b.start).getTime();
      return aStart - bStart;
    });

    sortedSpans.forEach(span => {
      const spanStart = new Date(span.start).getTime();
      const spanEnd = span.end ? new Date(span.end).getTime() : Date.now();

      let assignedRow = -1;
      for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
        if (!rowOccupancies[rowIndex]) {
          rowOccupancies[rowIndex] = [];
        }

        const overlaps = rowOccupancies[rowIndex].some(
          occupied => spanStart < occupied.end && spanEnd > occupied.start,
        );

        if (!overlaps) {
          assignedRow = rowIndex;
          rowOccupancies[rowIndex].push({ start: spanStart, end: spanEnd });
          break;
        }
      }

      if (assignedRow === -1) {
        assignedRow = Math.min(maxRows - 1, rowOccupancies.length);
        if (!rowOccupancies[assignedRow]) {
          rowOccupancies[assignedRow] = [];
        }
        rowOccupancies[assignedRow].push({ start: spanStart, end: spanEnd });
      }

      rowAssignments.set(span.span_id, assignedRow);
    });

    return rowAssignments;
  };

  const rowAssignments = assignRows(spans);

  return (
    <div className="relative h-full pt-5">
      {spans.map(span => {
        const rowIndex = rowAssignments.get(span.span_id) ?? 0;

        const position = calculateWaterfallPosition(span, traceStartTime, traceDuration);
        const status = spanStatuses.get(span.span_id) || SpanStatus.Success;
        const hasError = status === SpanStatus.Error || status === SpanStatus.Failed;
        const nodeType = getSpanDisplayType(span);
        const config = nodeType ? getNodeTypeConfig(nodeType, "h-2 w-2") : null;
        const { bgColor: configBgColor } = config || {};

        // Map Tailwind bg-*-50 classes to hex colors (500 variants)
        const tailwindToHex: Record<string, string> = {
          "bg-red-50": "#ef4444", // bg-red-500
          "bg-amber-50": "#f59e0b", // bg-amber-500
          "bg-blue-50": "#3b82f6", // bg-blue-500
          "bg-violet-50": "#8b5cf6", // bg-violet-500
          "bg-indigo-50": "#6366f1", // bg-indigo-500
          "bg-emerald-50": "#10b981", // bg-emerald-500
          "bg-cyan-50": "#06b6d4", // bg-cyan-500
          "bg-pink-50": "#ec4899", // bg-pink-500
          "bg-teal-50": "#14b8a6", // bg-teal-500
          "bg-orange-50": "#f97316", // bg-orange-500
          "bg-slate-50": "#64748b", // bg-slate-500
        };

        const getHexColor = (tailwindClass: string | undefined): string => {
          if (!tailwindClass) {
            return "#3b82f6"; // default to blue-500
          }
          return tailwindToHex[tailwindClass] || "#3b82f6";
        };

        let backgroundColor: string;

        if (hasError) {
          backgroundColor = "#ef4444"; // bg-red-500
        } else if (configBgColor) {
          backgroundColor = getHexColor(configBgColor);
        } else if (span.request_log_id) {
          backgroundColor = "#f59e0b"; // bg-amber-500
        } else {
          backgroundColor = "#3b82f6"; // bg-blue-500
        }

        const spanDuration = span.end
          ? new Date(span.end).getTime() - new Date(span.start).getTime()
          : Date.now() - new Date(span.start).getTime();

        const isHovered = hoveredSpanId === span.span_id;
        let opacity = 0.7;
        if (hasError) {
          opacity = 1;
        } else if (isHovered) {
          opacity = 1;
        }

        if (!backgroundColor) {
          return null;
        }

        return (
          <BasicTooltip
            delayDuration={0}
            variant="white"
            side="top"
            offset={4}
            contentClassName="shadow-lg [&>svg]:!fill-white [&>svg]:!stroke-gray-200"
            content={<SpanTooltip span={span} spanDuration={spanDuration} status={status} />}
          >
            <div
              key={span.span_id}
              className={cn(
                "absolute cursor-pointer rounded-sm transition-all hover:opacity-100 hover:ring-2 hover:ring-blue-400 hover:ring-offset-1",
              )}
              style={{
                left: position.left,
                width: position.width,
                top: `${rowIndex * rowHeight}px`,
                height: `${rowHeight - 1}px`,
                backgroundColor,
                opacity,
                minWidth: "2px",
                zIndex: isHovered ? 10 : 1,
              }}
              onMouseEnter={() => setHoveredSpanId(span.span_id)}
              onMouseLeave={() => setHoveredSpanId(null)}
              onClick={() => handleSpanClick(span)}
            />
          </BasicTooltip>
        );
      })}
    </div>
  );
};
